import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { getInstructorAvailability } from "hooks/api/schedule-v2";
import { InstructorAvailability } from "shared/types";
import { useDebouncedValue } from "./useDebouncedValue";

interface UseInstructorAvailabilityParams {
  instructorId: string | undefined;
  classId: string | undefined;
  startDate: string | undefined; // YYYY-MM-DD
  rrule: string | undefined;
  timezone: string;
  // "Enough input to build a valid, bounded probe" — mirrors whatever
  // checkIfValidRRule/buildRRule themselves require (e.g. recurrence_days
  // non-empty for a weekly pattern), not a hand-picked subset of it. Owned
  // by the caller, which already has this logic for real submission.
  sufficientInput: boolean;
  // The real gating condition, mirroring the backend's own
  // employee_management_enabled && sub_flag structure — not just "any of
  // the three sub-flags on their own." If false, the endpoint would only
  // ever return a fully-unrestricted response, so skip the round trip.
  anyRuleActive: boolean;
}

const DEBOUNCE_MS = 300;

/**
 * Read-only preview of instructor-availability rules for a proposed,
 * not-yet-saved schedule (POST /v2/schedule/availability) — powers
 * TimeSection's proactive time-picker restriction. Debounces on the query
 * KEY (not a manually-managed fetch+setState), so react-query's own
 * request-cancellation/staleness handling applies: a slow-but-stale response
 * can't land after a newer one and silently overwrite it with data for an
 * instructor/pattern combination that's no longer even selected.
 */
export const useInstructorAvailability = ({
  instructorId,
  classId,
  startDate,
  rrule,
  timezone,
  sufficientInput,
  anyRuleActive,
}: UseInstructorAvailabilityParams) => {
  const debouncedKey = useDebouncedValue(
    { instructorId, classId, startDate, rrule, timezone },
    DEBOUNCE_MS,
  );

  const enabled =
    sufficientInput &&
    anyRuleActive &&
    !!debouncedKey.instructorId &&
    !!debouncedKey.classId &&
    !!debouncedKey.startDate;

  return useQuery<InstructorAvailability>({
    queryKey: ["instructor-availability", debouncedKey],
    queryFn: () =>
      getInstructorAvailability({
        instructor_id: debouncedKey.instructorId as string,
        class_id: debouncedKey.classId as string,
        start_date: debouncedKey.startDate as string,
        rrule: debouncedKey.rrule,
        timezone: debouncedKey.timezone,
      }),
    enabled,
    // Keeps showing the previous successful result while a new key's fetch
    // is in flight, instead of a moment of "no data yet" reading as
    // "everything disabled" on every keystroke-driven refetch.
    placeholderData: keepPreviousData,
    retry: 1,
  });
};
